import { ComponentProps, useMemo } from 'react';
import omit from 'lodash/omit';

import { ID } from '../../../../../types';
import { FetchLifestyleDetection } from '../../../../lifestyleDetections/queries/lifestyleDetectionsByImageUrl.query';
import {
  FetchProductsCacheKey,
  FetchProductsFilters,
  FetchProductsSortTypes
} from '../../../../products/productsTypes';

import { CACHE_FETCH_PRODUCTS_QUERY } from '../../../../products/queries/fetchCacheProducts.query';

import { useCacheProducts } from '../../../../products/hooks/useCacheProducts';

import {
  ThreeDStockProductsList,
  ThreeDStockProductsListProduct
} from '../../lists/ThreeDStockProductsList';

interface ThreeDStockDetectedObjectMatchesProps {
  detection: FetchLifestyleDetection;
  initialFilters: FetchProductsFilters;
  lifestyleId?: ID;
  cacheKey: FetchProductsCacheKey;
  listProps: ComponentProps<typeof ThreeDStockProductsList>;
}

function ThreeDStockDetectedObjectMatches({
  detection,
  initialFilters,
  lifestyleId,
  cacheKey,
  listProps
}: ThreeDStockDetectedObjectMatchesProps) {
  const filters = useMemo<FetchProductsFilters>(
    () => ({
      ...omit(initialFilters, [
        'similarImage',
        'similarProduct',
        'similarProductImageId',
        'similarProductsByCustomArea',
        'similarProductsByObjectInLifestyle',
        'searchByDetectedId',
        'searchByDetectedIds'
      ]),
      searchByDetectedId: { detectionId: detection.id, lifestyleId }
    }),
    [detection.id, initialFilters, lifestyleId]
  );

  const {
    products,
    productsError,
    productsFetched,
    productsIsPlaceholderData,
    productsTotalCount,
    productsPage,
    productsLimit,
    hasNextProductsPage,
    loadMoreProducts,
    productsFetchingNextPage,
    productsFilters,
    changeProductsFilters
  } = useCacheProducts<ThreeDStockProductsListProduct>({
    cacheKey: `${cacheKey}-detected-object-matches-${JSON.stringify([
      detection.id,
      detection.detectRequestId,
      lifestyleId,
      filters
    ])}`,
    query: CACHE_FETCH_PRODUCTS_QUERY,
    cacheQuery: CACHE_FETCH_PRODUCTS_QUERY,
    defaultApi: 'cache_api',
    initialFilters: filters,
    initialSort: [FetchProductsSortTypes.SCORE_DESC],
    initialLimit: 5,
    trackTotalHits: true,
    options: {
      withoutPrefetch: true
    }
  });

  return (
    <ThreeDStockProductsList
      {...listProps}
      products={products}
      productsError={productsError}
      productsFetched={productsFetched}
      productsIsPlaceholderData={productsIsPlaceholderData}
      productsTotalCount={productsTotalCount}
      productsPage={productsPage}
      productsLimit={productsLimit}
      hasNextProductsPage={hasNextProductsPage}
      loadMoreProducts={loadMoreProducts}
      productsFetchingNextPage={productsFetchingNextPage}
      productsFilters={productsFilters}
      changeProductsFilters={changeProductsFilters}
    />
  );
}

export default ThreeDStockDetectedObjectMatches;
